import { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import React from 'react';
import * as styles from './useTooltip.css';

interface TooltipOptions {
  /** ツールチップのオフセット（デフォルト: { x: 0, y: 0 }） */
  offset?: { x: number; y: number };
  /** カスタムクラス名 */
  className?: string;
  /** 表示までの遅延時間（ミリ秒、デフォルト: 0） */
  delay?: number;
}

/**
 * React Portal-basedのツールチップフック
 *
 * HTML要素で使える汎用的なツールチップ。マウスポインタの位置に表示される。
 *
 * @param options ツールチップのオプション設定
 * @returns show, hide関数とTooltipコンポーネント
 *
 * @example
 * ```typescript
 * const { show, hide, Tooltip } = useTooltip();
 *
 * return (
 *   <>
 *     <div
 *       onMouseEnter={(e) => show("Tooltip content", e)}
 *       onMouseLeave={hide}
 *     >
 *       Hover me
 *     </div>
 *     <Tooltip />
 *   </>
 * );
 * ```
 *
 * @example
 * ```typescript
 * // React Nodeをコンテンツとして渡す
 * const { show, hide, Tooltip } = useTooltip();
 *
 * const content = (
 *   <div>
 *     <strong>Title</strong>
 *     <p>Description</p>
 *   </div>
 * );
 *
 * return (
 *   <>
 *     <button
 *       onMouseEnter={(e) => show(content, e)}
 *       onMouseLeave={hide}
 *     >
 *       Hover me
 *     </button>
 *     <Tooltip />
 *   </>
 * );
 * ```
 */
export function useTooltip(options: TooltipOptions = {}) {
  const { offset = { x: 0, y: 0 }, className, delay = 0 } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [content, setContent] = useState<React.ReactNode>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * ツールチップを表示
   * @param content 表示する内容（文字列またはReact Node）
   * @param event マウスイベント
   */
  const show = useCallback(
    (content: React.ReactNode, event: React.MouseEvent) => {
      // 既存のタイマーをクリア
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const showTooltip = () => {
        setContent(content);
        setPosition({
          x: event.clientX + offset.x,
          y: event.clientY + offset.y,
        });
        setIsVisible(true);
      };

      if (delay > 0) {
        timerRef.current = setTimeout(showTooltip, delay);
      } else {
        showTooltip();
      }
    },
    [offset.x, offset.y, delay]
  );

  /**
   * ツールチップを非表示
   */
  const hide = useCallback(() => {
    // タイマーをクリア
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  }, []);

  /**
   * ツールチップコンポーネント
   * React Portalを使用してbody直下にレンダリング
   */
  const Tooltip = useCallback(() => {
    if (!isVisible) return null;

    return createPortal(
      <div
        className={className || styles.tooltip}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        {content}
      </div>,
      document.body
    );
  }, [isVisible, position.x, position.y, content, className]);

  return { show, hide, Tooltip };
}
